import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

type User = {
  id: string;
  email: string;
  fullName?: string;
  role: string;
};

type AuthContextType = {
  user: User | null;
  login: (user: User) => void;
  updateUser: (patch: Partial<User>) => void;
  logout: () => void;
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // The JWT lives in an httpOnly cookie the browser sends automatically.
  // On load we verify the session with the API rather than trusting any
  // client-side state — a stale/expired cookie resolves to logged-out.
  //
  // IMPORTANT: this fetch is fire-and-forget for page rendering.
  // Pages must NOT gate their content on `isLoading` from useAuth() —
  // prerendered routes (Home, Locations, Privacy, Terms) need to show
  // their content immediately, and only auth-dependent UI (Profile
  // button, MyStamps personalized view) should wait on the result.
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await api.get('/auth/me');
      if (!alive) return;
      setUser(res?.user ?? null);
      setIsLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // The cookie is already set by the API on login/google — here we
  // only mirror the user into React state.
  const login = (user: User) => {
    setUser(user);
  };

  const updateUser = (patch: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  // Clear the server-side cookie, then drop local state.
  const logout = () => {
    setUser(null);
    void api.post('/auth/logout', {});
  };

  return (
    <AuthContext.Provider value={{ user, login, updateUser, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
